<template>
  <form>
    <FormHeader :title="title" @cancel="onCancel" @submit="onSubmit"/>
    <div class="space-y-12 mt-5">
      <div>
        <h3 class="text-base/7 font-semibold text-gray-900">{{ $gettext("Détails de l\'offre d'achat") }}</h3>
        <FormDateField v-model="signatureDate" field-path="attributes.signatureDate" :field-label="$gettext('Date de l\'offre')" :field-attr="signatureDateAttrs" :errors="errors" />
        <FormMoneyField v-model="price" field-path="attributes.price" :field-label="$gettext('Montant de l\u2019offre')" :field-attrs="priceAttrs" :errors="errors" />
        <div class="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-2">
          <div class="sm:col-span-3">
            <FormComboboxField v-model="notaryId" field-path="relationships.notary.data.id" :field-label="$gettext('Notaire')" :field-attrs="notaryIdAttrs" :errors="errors" :options="notaryOptions" />
            <div class="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4">
              <div></div>
              <button type="button" class="w-fit rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-indigo-500" @click="showCreateNotaryModal = true">
                + {{ $gettext("Créer un notaire") }}
              </button>
            </div>
          </div>
        </div>
        <CreateNotaryModal
          :open="showCreateNotaryModal"
          @close="showCreateNotaryModal = false"
          @created="onNotaryCreated"
        />
        <FormCustomerComboboxField v-model="buyers" field-path="relationships.buyers.data" :field-label="$gettext('Acheteur(s)')" :field-attrs="buyersAttrs" :errors="errors"/>

        <div class="mt-8 rounded-lg border border-gray-300 bg-white p-6 shadow-sm">
          <h3 class="text-base/7 font-semibold text-gray-900">{{ $gettext("Questions permettant de déterminer le profil de risque de la transaction") }}</h3>

          <UnusualClientCharacteristicsBlock
            v-model:characteristics="unusualClientCharacteristics"
            v-model:reasons="unusualClientReasons"
            v-model:explanation="unusualClientExplanation"
            :characteristicsAttrs="unusualClientCharacteristicsAttrs"
            :explanationAttrs="unusualClientExplanationAttrs"
            :errors="errors"
          />

          <div v-if="hasCompanyBuyer" class="border-t border-gray-200 pt-6 mt-6">
            <FormSelectField
              v-model="equityBelowPrice"
              field-path="attributes.equityBelowPrice"
              :field-label="$gettext('Les capitaux propres de l\'entreprise sont-ils inf\u00e9rieurs au prix d\u2019achat du bien ?')"
              :field-description="$gettext('Les capitaux propres figurent dans les derniers comptes annuels publiés (BCE / Banque Nationale). Exemple : une société aux capitaux propres de 50 000 € qui achète un bien à 400 000 € — l\'origine des 350 000 € complémentaires doit être clarifiée.')"
              :field-attrs="equityBelowPriceAttrs"
              :errors="errors"
              :options="equityBelowPriceOptions"
            />
            <FormTextareaField
              v-if="equityBelowPrice === 'not_applicable'"
              v-model="equityNotApplicableReason"
              field-path="attributes.equityNotApplicableReason"
              :field-label="$gettext('Raison')"
              :field-attrs="equityNotApplicableReasonAttrs"
              :errors="errors"
            />
            <FormBooleanField
              v-if="equityBelowPrice === 'yes'"
              v-model="financedByCredit"
              field-path="attributes.financedByCredit"
              :field-label="$gettext('L\'entreprise finance-t-elle la transaction par un emprunt ?')"
              :field-description="$gettext('Un crédit bancaire réduit le risque, la banque ayant mené ses propres vérifications. Exemple : un achat financé à 80 % par un crédit d\'investissement est moins sensible qu\'un paiement intégral sur des fonds dont l\'origine est inconnue.')"
              :field-attrs="financedByCreditAttrs"
              :errors="errors"
            />
          </div>

          <div v-if="hasPhysicalPersonBuyer" class="border-t border-gray-200 pt-6 mt-6">
            <FormBooleanField
              v-model="cashPayment"
              field-path="attributes.cashPayment"
              :field-label="$gettext('Une des parties a-t-elle eu l\'intention de payer ou recevoir des espèces ?')"
              :field-description="$gettext('La loi belge interdit tout paiement en espèces pour une vente immobilière, acompte compris. Exemple : un acheteur qui propose de verser l\'acompte en liquide — à signaler même si le paiement n\'a finalement pas eu lieu.')"
              :field-attrs="cashPaymentAttrs"
              :errors="errors"
            />

            <FormSelectField
              v-model="transactionDisproportionate"
              field-path="attributes.transactionDisproportionate"
              class="mt-6"
              :field-label="$gettext('La transaction présente-t-elle une disproportion par rapport à la situation socio-économique d\'une des parties ?')"
              :field-description="$gettext('Exemples : acquisition d\'un bien de grande valeur sans revenus ou patrimoine connus cohérents, apport personnel sans rapport avec la situation professionnelle déclarée.')"
              :field-attrs="transactionDisproportionateAttrs"
              :errors="errors"
              :options="transactionDisproportionateOptions"
            />
            <FormTextareaField
              v-if="transactionDisproportionate === 'not_applicable'"
              v-model="disproportionNotApplicableReason"
              field-path="attributes.disproportionNotApplicableReason"
              :field-label="$gettext('Raison')"
              :field-attrs="disproportionNotApplicableReasonAttrs"
              :errors="errors"
            />

            <FormSelectField
              v-model="priceMarketConsistency"
              field-path="attributes.priceMarketConsistency"
              class="mt-6"
              :field-label="$gettext('Le prix de la transaction est-il cohérent avec le prix du marché ?')"
              :field-description="$gettext('À titre indicatif : un écart de plus de 25 % par rapport au prix du marché estimé au moment de la vente est considéré comme inhabituel. Exemple : un bien estimé 300 000 € vendu 220 000 €, soit un écart d\'environ 27 %.')"
              :field-attrs="priceMarketConsistencyAttrs"
              :errors="errors"
              :options="priceMarketConsistencyOptions"
            />
          </div>
        </div>
      </div>

      <div>
        <h3 class="text-base/7 font-semibold text-gray-900">{{ $gettext("Documents à importer") }}</h3>
        <FormDocumentsField
          v-model="purchaseOfferDocuments"
          category-name="signedPurchaseOffer"
          field-path="relationships.purchaseOfferDocuments.data"
          :field-label="$gettext('Offre d\'achat signée')"
          field-description=""
          :field-attrs="purchaseOfferDocumentsAttrs" :errors="errors"/>
        <FormDocumentsField
          v-model="purchaseOfferDocuments"
          category-name="identityDocuments"
          field-path="relationships.purchaseOfferDocuments.data"
          :field-label="$gettext('Documents d\'identité')"
          field-description=""
          :field-attrs="purchaseOfferDocumentsAttrs" :errors="errors"/>
      </div>
    </div>
    <FormFooter @cancel="onCancel" @submit="onSubmit"/>
  </form>
</template>

<script setup>
import { useForm } from "vee-validate";
import * as yup from "yup";
import { watch, computed, ref } from "vue";
import FormHeader from "@/components/form-fields/FormHeader.vue";
import FormFooter from "@/components/form-fields/FormFooter.vue";
import { useGettext } from "vue3-gettext";
import FormMoneyField from "@/components/form-fields/FormMoneyField.vue";
import FormCustomerComboboxField from "../form-fields/FormCustomerComboboxField.vue";
import { useNotaryStore } from "@/stores/notary";
import FormComboboxField from "../form-fields/FormComboboxField.vue";
import FormDateField from "../form-fields/FormDateField.vue";
import FormDocumentsField from "../form-fields/FormDocumentsField.vue";
import FormSelectField from "../form-fields/FormSelectField.vue";
import FormTextareaField from "../form-fields/FormTextareaField.vue";
import FormBooleanField from "../form-fields/FormBooleanField.vue";
import CreateNotaryModal from "@/components/notaries/CreateNotaryModal.vue";
import UnusualClientCharacteristicsBlock from "@/components/unusual-client/UnusualClientCharacteristicsBlock.vue";

const { $gettext } = useGettext();
const notaryStore = useNotaryStore();

const props = defineProps(["purchaseOffer", "title"]);
const emits = defineEmits(["submit", "cancel"]);

const validationSchema = yup.object({
  attributes: yup.object({
    price: yup.number().required($gettext("est obligatoire")),
    signatureDate: yup.date().required($gettext("est obligatoire")),
    equityBelowPrice: yup.string().nullable(),
    equityNotApplicableReason: yup.string().nullable()
      .when("equityBelowPrice", {
        is: "not_applicable",
        then: (schema) => schema.required($gettext("est obligatoire"))
      }),
    financedByCredit: yup.boolean().nullable()
      .when("equityBelowPrice", {
        is: "yes",
        then: (schema) => schema.required($gettext("est obligatoire"))
      }),
    cashPayment: yup.boolean().nullable(),
    transactionDisproportionate: yup.string().nullable(),
    disproportionNotApplicableReason: yup.string().nullable()
      .when("transactionDisproportionate", {
        is: "not_applicable",
        then: (schema) => schema.required($gettext("est obligatoire"))
      }),
    priceMarketConsistency: yup.string().nullable(),
    unusualClientCharacteristics: yup.string().oneOf(["yes", "no"]).nullable(),
    unusualClientReasons: yup
      .array()
      .of(yup.string())
      .when("unusualClientCharacteristics", {
        is: "yes",
        then: (schema) => schema.min(1, $gettext("au moins 1 motif obligatoire")),
        otherwise: (schema) => schema.nullable()
      }),
    unusualClientExplanation: yup.string().nullable()
  }),
  relationships: yup.object({
    buyers: yup.object({
      data: yup.array().of(yup.object({
        id: yup.string()
      })).min(1, $gettext("au moins 1 obligatoire"))
    })
  })
});

const { errors, handleSubmit, defineField, setValues, setFieldValue } = useForm({
  validationSchema: validationSchema,
  initialValues: props.purchaseOffer
});

const [price, priceAttrs] = defineField("attributes.price");
const [buyers, buyersAttrs] = defineField("relationships.buyers.data");
const [notaryId, notaryIdAttrs] = defineField("relationships.notary.data.id");
const [signatureDate, signatureDateAttrs] = defineField("attributes.signatureDate");
const [purchaseOfferDocuments, purchaseOfferDocumentsAttrs] = defineField("relationships.purchaseOfferDocuments.data");
const [equityBelowPrice, equityBelowPriceAttrs] = defineField("attributes.equityBelowPrice");
const [equityNotApplicableReason, equityNotApplicableReasonAttrs] = defineField("attributes.equityNotApplicableReason");
const [financedByCredit, financedByCreditAttrs] = defineField("attributes.financedByCredit");
const [cashPayment, cashPaymentAttrs] = defineField("attributes.cashPayment");
const [transactionDisproportionate, transactionDisproportionateAttrs] = defineField("attributes.transactionDisproportionate");
const [disproportionNotApplicableReason, disproportionNotApplicableReasonAttrs] = defineField("attributes.disproportionNotApplicableReason");
const [priceMarketConsistency, priceMarketConsistencyAttrs] = defineField("attributes.priceMarketConsistency");
const [unusualClientCharacteristics, unusualClientCharacteristicsAttrs] = defineField("attributes.unusualClientCharacteristics");
const [unusualClientReasons] = defineField("attributes.unusualClientReasons");
const [unusualClientExplanation, unusualClientExplanationAttrs] = defineField("attributes.unusualClientExplanation");

const equityBelowPriceOptions = [
  { value: "yes", label: $gettext("Oui") },
  { value: "no", label: $gettext("Non") },
  { value: "not_applicable", label: $gettext("Ne s'applique pas") }
];

const transactionDisproportionateOptions = [
  { value: "yes", label: $gettext("Oui") },
  { value: "no", label: $gettext("Non") },
  { value: "not_applicable", label: $gettext("Ne s'applique pas") }
];

const priceMarketConsistencyOptions = [
  { value: "yes", label: $gettext("Oui") },
  { value: "no", label: $gettext("Non") },
  { value: "i_dont_know", label: $gettext("Je ne sais pas") }
];

const showCreateNotaryModal = ref(false);

const notaryOptions = computed(() =>
  notaryStore.sorted.map((notary) => ({
    value: notary.id,
    label: "(" + notary.attributes.zipcode + ") " + notary.attributes.name,
  }))
);

const onNotaryCreated = (created) => {
  if (created?.id) notaryId.value = created.id;
};

const hasCompanyBuyer = computed(() => {
  if (!Array.isArray(buyers.value)) return false;
  return buyers.value.some((buyer) => buyer.type === "company");
});

const hasPhysicalPersonBuyer = computed(() => {
  if (!Array.isArray(buyers.value)) return false;
  return buyers.value.some((buyer) => buyer.type === "physicalPerson");
});

watch(hasCompanyBuyer, (newVal, oldVal) => {
  if (oldVal && !newVal) {
    setFieldValue("attributes.equityBelowPrice", null);
    setFieldValue("attributes.equityNotApplicableReason", null);
    setFieldValue("attributes.financedByCredit", null);
  }
});

watch(hasPhysicalPersonBuyer, (newVal, oldVal) => {
  if (oldVal && !newVal) {
    setFieldValue("attributes.cashPayment", null);
    setFieldValue("attributes.transactionDisproportionate", null);
    setFieldValue("attributes.disproportionNotApplicableReason", null);
    setFieldValue("attributes.priceMarketConsistency", null);
  }
});

watch(transactionDisproportionate, (newVal) => {
  if (newVal !== "not_applicable") {
    setFieldValue("attributes.disproportionNotApplicableReason", null);
  }
});

const stripCustomerObjects = (customers) => {
  if (!Array.isArray(customers)) return customers;
  return customers.map(({ id, type }) => ({ id, type }));
};

const onFormSubmit = ((values, actions) => {
  const payload = {
    ...values,
    relationships: {
      ...values.relationships,
      buyers: {
        ...values.relationships?.buyers,
        data: stripCustomerObjects(values.relationships?.buyers?.data)
      }
    }
  };
  emits("submit", payload, actions);
});

const onSubmit = (() => {
  handleSubmit(onFormSubmit)();
});

const onCancel = (() => {
  emits("cancel");
});

watch(() => props.purchaseOffer, (newPurchaseOffer) => {
  setValues(newPurchaseOffer);
});
</script>
